import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/Edit';
import React, {MouseEvent, useState} from 'react';
import {useMsal} from '../Auth/Msal';
import BookmarksDialog from '../Bookmarks/BookmarksDialog';
import {BookmarkLink, usePreferencesState} from '../Preferences/PreferencesProvider';
import {useModeState} from '../Theme/ThemeProvider';
import UserAvatar from './UserAvatar';

const useStyles = makeStyles(theme => ({
    userInfo: {
        padding: theme.spacing(2),
    },
}));

type Props = {
    menuItems ?: Array<{
        title : string;
        callback : () => void;
    }>;
};

const UserMenu : React.FC<Props> = ({menuItems} : Props) => {
    const classes = useStyles();
    const msal = useMsal();
    const {linkGroups} = usePreferencesState().preferences.dashboard;
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    const [bookmarksDialogOpen, setBookmarksDialogOpen] = useState(false);
    const modeState = useModeState();

    const handleMenuOpen = (event : MouseEvent<HTMLButtonElement>) => {
        setAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchor(null);
    };

    const favorites = linkGroups.reduce(
        (favorites : BookmarkLink[], linkGroup) => [...favorites, ...linkGroup.links.filter(link => link.favorite)],
        []
    );

    return (
        <>
            <IconButton edge="end" onClick={handleMenuOpen}>
                <UserAvatar account={msal.account}/>
            </IconButton>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
            >
                <div className={classes.userInfo}>
                    <Typography>
                        {msal.account.name}
                        {' '}
                        <IconButton size="small" onClick={() => window.open('https://delve.office.com/?v=editprofile')}>
                            <EditIcon fontSize="small"/>
                        </IconButton>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">{msal.account.username}</Typography>
                </div>

                <Divider/>

                <MenuList>
                    {menuItems && (
                        menuItems.map((menuItem, index) => (
                            <MenuItem key={index} onClick={() => {
                                handleMenuClose();
                                menuItem.callback();
                            }}>{menuItem.title}</MenuItem>
                        ))
                    )}

                    <MenuItem onClick={() => {
                        handleMenuClose();
                        setBookmarksDialogOpen(true);
                    }}>Bookmarks</MenuItem>
                </MenuList>

                <Divider/>

                {favorites.length > 0 && (
                    <>
                        <MenuList>
                            {favorites.map((link, index) => (
                                <MenuItem key={index} onClick={() => {
                                    handleMenuClose();
                                    link.openInNew ? window.open(link.url) : window.location.href = link.url;
                                }}>
                                    {link.title}
                                </MenuItem>
                            ))}
                        </MenuList>

                        <Divider/>
                    </>
                )}

                <MenuList>
                    <MenuItem>
                        <FormControlLabel
                            control={<Switch
                                checked={modeState.mode === 'dark'}
                                onChange={event => modeState.setMode(event.target.checked ? 'dark' : 'light')}
                            />}
                            label="Dark mode"
                        />
                    </MenuItem>
                </MenuList>

                <Divider/>

                <MenuList>
                    <MenuItem onClick={() => {
                        handleMenuClose();
                        msal.logout();
                    }}>Sign out</MenuItem>
                </MenuList>
            </Popover>

            <BookmarksDialog open={bookmarksDialogOpen} onClose={() => setBookmarksDialogOpen(false)}/>
        </>
    );
};

export default UserMenu;
